import React from "react"
import { useTranslation } from "react-i18next"
import { Dialog, Transition } from "@headlessui/react"

import { CrossIcon } from "components/Elements"
import "./Modal.scoped.scss"
import { isNotEmpty } from "utils"

export type ModalProps = {
	title?: string
	isOpen: boolean
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
	className?: string
	children?: React.ReactNode
	onClose?: () => void
	isPreventClosing?: boolean
}

export const Modal: React.FC<ModalProps> = ({
	title,
	isOpen,
	setIsOpen,
	className = "",
	children,
	onClose,
	isPreventClosing = false,
}) => {
	const { t } = useTranslation()
	const modalPreviousState = React.useRef(false)

	React.useEffect(() => {
		// Trigger onClose callback each time the modal is closed
		// in whatever way
		const wasOpen = modalPreviousState.current
		const modalIsClosing = wasOpen && !isOpen
		if (modalIsClosing && onClose) onClose()
		modalPreviousState.current = isOpen
	}, [isOpen, onClose])

	return (
		<Transition appear show={isOpen} as={React.Fragment}>
			<Dialog
				open={isOpen}
				onClose={() => (isPreventClosing ? null : setIsOpen(false))}
				as="div"
				className="c-modal inset-0"
			>
				<div className="min-h-screen px-4 text-center">
					<Dialog.Overlay className="c-modal__overlay inset-0" />

					{/* This element is to trick the browser into centering the modal contents. */}
					<span className="inline-block h-screen align-middle" aria-hidden="true">
						&#8203;
					</span>
					<Transition.Child
						as={React.Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<div
							className={`c-modal__container md:px-10 px-5 py-10 my-8 transition-all transform shadow-xl ${className}`}
						>
							{!isPreventClosing && (
								<button
									onClick={() => setIsOpen(false)}
									aria-label={t("close_modal", {
										ns: "bike",
									})}
									type="button"
									className="c-modal__close p-2"
								>
									<CrossIcon size="12" color="var(--primaryColor)" />
								</button>
							)}
							{isNotEmpty(title) && (
								<Dialog.Title className="c-h2 text-primary mb-8 pr-6 md:pr-0">
									{title}
								</Dialog.Title>
							)}

							{children}
						</div>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	)
}
